import { useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  ExportDrawer,
  SearchInput,
  Select,
  groupRows,
} from '@/components/ui'
import { useCustomerDemand } from '../api/queries'
import type { CustomerDemandRow } from '../api/types'
import { CUSTOMER_DEMAND_COLUMNS } from '../utils/csv'

type Tab = 'customer-orders' | 'samples'

function DateRangePicker({
  from,
  to,
  onFromChange,
  onToChange,
}: {
  from: string
  to: string
  onFromChange: (v: string) => void
  onToChange: (v: string) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
      />
      <span className="text-role-metadata">to</span>
      <input
        type="date"
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        className="rounded border border-border bg-surface px-2 py-1.5 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
      />
    </div>
  )
}

function defaultRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  }
}

function soStatusVariant(state: string): 'success' | 'info' | 'neutral' {
  if (state === 'sale') return 'success'
  if (state === 'done') return 'info'
  return 'neutral'
}

function soStatusLabel(state: string): string {
  const map: Record<string, string> = { sale: 'Confirmed', done: 'Complete', draft: 'Draft' }
  return map[state] ?? state
}

const demandColumns: ColumnDef<CustomerDemandRow>[] = [
  {
    id: 'so_name',
    header: 'SO #',
    accessorKey: 'so_name',
    cell: (ctx) => {
      const v = ctx.getValue() as string
      return v ? (
        <span className="text-role-data-primary">{v}</span>
      ) : (
        <span className="text-role-metadata">—</span>
      )
    },
  },
  {
    id: 'customer_name',
    header: 'Customer',
    accessorKey: 'customer_name',
    cell: (ctx) => <span className="text-role-table-cell">{ctx.getValue() as string}</span>,
  },
  {
    id: 'qty_ordered',
    header: 'Qty Ordered',
    accessorKey: 'qty_ordered',
    cell: (ctx) => <span className="text-role-table-cell">{ctx.getValue() as number}</span>,
  },
  {
    id: 'qty_delivered',
    header: 'Qty Delivered',
    accessorKey: 'qty_delivered',
    cell: (ctx) => <span className="text-role-table-cell">{ctx.getValue() as number}</span>,
  },
  {
    id: 'delivery_date',
    header: 'Delivery Date',
    accessorKey: 'delivery_date',
    cell: (ctx) => {
      const v = ctx.getValue() as string | null
      return <span className="text-role-metadata">{v ? v.slice(0, 10) : '—'}</span>
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: (ctx) => {
      const v = ctx.getValue() as string
      return (
        <Chip variant={soStatusVariant(v)} size="xs">
          {soStatusLabel(v)}
        </Chip>
      )
    },
  },
]

function CustomerOrdersView({
  rows,
  activeTab,
}: {
  rows: CustomerDemandRow[]
  activeTab: Tab
}) {
  const [search, setSearch] = useState('')
  const [productFilter, setProductFilter] = useState('')

  // Pre-filter by line_type based on active tab
  const tabRows = useMemo(
    () => rows.filter((r) => activeTab === 'customer-orders' ? r.line_type !== 'sample' : r.line_type === 'sample'),
    [rows, activeTab],
  )

  // Product options scoped to rows visible on this tab
  const productOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of tabRows) {
      const key = r.product_code || r.product_name
      if (!seen.has(key)) seen.set(key, r.product_code ? `${r.product_code}  ${r.product_name}` : r.product_name)
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [tabRows])

  // Reset product filter when tab changes (stale selection from other tab)
  const filtered = useMemo(() => {
    let result = tabRows
    if (productFilter) result = result.filter((r) => (r.product_code || r.product_name) === productFilter)
    const needle = search.trim().toLowerCase()
    if (needle) result = result.filter(
      (r) =>
        String(r.product_code ?? '').toLowerCase().includes(needle) ||
        String(r.product_name ?? '').toLowerCase().includes(needle) ||
        String(r.customer_name ?? '').toLowerCase().includes(needle) ||
        String(r.so_name ?? '').toLowerCase().includes(needle),
    )
    return result
  }, [tabRows, search, productFilter])

  const showGroups = !productFilter

  const groups = useMemo(
    () => showGroups ? groupRows(filtered, {
      keyOf: (r) => r.product_code || r.product_name,
      labelOf: (key) => {
        const match = filtered.find((r) => (r.product_code || r.product_name) === key)
        return match ? (match.product_code ? `${match.product_code}  ${match.product_name}` : match.product_name) : key
      },
    }) : undefined,
    [filtered, showGroups],
  )

  if (!tabRows.length) {
    return (
      <EmptyState
        variant="no-data"
        title={activeTab === 'customer-orders' ? 'No orders' : 'No samples'}
        description="No sales orders found for Uviva-supplied products in the selected date range."
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Select
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          className="w-64"
        >
          <option value="">All products</option>
          {productOptions.map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </Select>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer or SO #…"
          containerClassName="flex-1 max-w-none"
        />
      </div>

      {!filtered.length && (
        <EmptyState
          variant="no-match"
          title="No matches"
          description="Try a different product, customer, or SO number."
        />
      )}

      {!!filtered.length && (
        <DataTable
          moduleId={activeTab === 'customer-orders' ? 'uviva-demand-commercial' : 'uviva-demand-samples'}
          columns={demandColumns}
          data={filtered}
          groups={groups}
        />
      )}
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'customer-orders', label: 'Customer Orders' },
  { key: 'samples', label: 'Samples' },
]

export function UvivaOperationsPage() {
  const [tab, setTab] = useState<Tab>('customer-orders')
  const [exportOpen, setExportOpen] = useState(false)
  // Default to the last 30 days; the pickers widen/clear the range from there.
  const range = defaultRange()
  const [fromDate, setFromDate] = useState(range.from)
  const [toDate, setToDate] = useState(range.to)

  // High limit so a widened (e.g. all-time) range isn't silently capped at 200.
  const customerDemand = useCustomerDemand(fromDate || undefined, toDate || undefined, 2000)

  // Same line_type split CustomerOrdersView uses per-tab — kept here too so
  // export matches the active tab rather than dumping both tabs combined.
  const exportRows = useMemo(
    () =>
      (customerDemand.data?.rows ?? []).filter((r) =>
        tab === 'customer-orders' ? r.line_type !== 'sample' : r.line_type === 'sample',
      ),
    [customerDemand.data, tab],
  )

  return (
    <div className="flex flex-col h-full bg-canvas">
      <header className="border-b border-border-subtle bg-surface px-6 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-role-page-title">Operations</h1>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExportOpen(true)}
            disabled={exportRows.length === 0}
            title="Pick columns or apply a saved template before download"
          >
            <Download size={12} /> Export…
          </Button>
        </div>

        {/* Sub-tabs */}
        <div className="mt-3 flex items-center gap-1 border-b border-border-subtle">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-accent text-accent'
                  : 'border-transparent text-secondary hover:text-primary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {/* Date range filter */}
      <div className="flex items-center gap-4 border-b border-border-subtle bg-surface px-6 py-2">
        <DateRangePicker
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
      </div>

      <div className="flex-1 overflow-auto p-4">
        {customerDemand.isLoading ? (
          <div className="text-role-metadata py-8 text-center">Loading…</div>
        ) : customerDemand.isError ? (
          <EmptyState
            variant="error"
            title="Could not load data"
            description="Check middleware logs for details."
          />
        ) : (
          <CustomerOrdersView
            rows={customerDemand.data?.rows ?? []}
            activeTab={tab}
          />
        )}
      </div>

      <ExportDrawer
        open={exportOpen}
        onOpenChange={setExportOpen}
        rows={exportRows}
        columns={CUSTOMER_DEMAND_COLUMNS}
        moduleId={tab === 'customer-orders' ? 'uviva-demand-commercial-export' : 'uviva-demand-samples-export'}
        filenameBase={tab === 'customer-orders' ? 'uviva-customer-orders' : 'uviva-samples'}
      />
    </div>
  )
}
